import { theme } from 'antd';

const { colorBorder, colorError, colorSuccess, colorWarning } = theme.defaultAlgorithm(theme.defaultSeed);

import { NewTicket } from '../../../model/types';

export const CHAT_APP_UI_STATES_SELECTABLE = {
    ONLINE: 'ONLINE', // sipState(ONLINE), msgType(STATE_CHANGE)
    OFFLINE: 'OFFLINE', // sipState(OFFLINE), msgType(STATE_CHANGE)
    AWAY: 'AWAY' // sipState(AWAY), msgType(STATE_CHANGE)
} as const;

export const CHAT_APP_UI_STATES = {
    ...CHAT_APP_UI_STATES_SELECTABLE,
    initializingError: 'initializingError'
} as const;

export type ChatAppUIStates = (typeof CHAT_APP_UI_STATES)[keyof typeof CHAT_APP_UI_STATES];

export const chatAppStateColorMap = {
    [CHAT_APP_UI_STATES.ONLINE]: colorSuccess,
    [CHAT_APP_UI_STATES.OFFLINE]: colorError,
    [CHAT_APP_UI_STATES.AWAY]: colorWarning,
    undefined: colorBorder
};
export const acceptedAudioTypeMap = {
    'audio/ogg': 'audio/ogg', // .oga
    'audio/mpeg': 'audio/mpeg', // .mp3
    'audio/wav': 'audio/wav', // .wav
    'audio/webm': 'audio/webm' // .weba
};
export const acceptedFileTypeMap = {
    'application/pdf': 'application/pdf',
    'application/vnd.oasis.opendocument.presentation': 'application/vnd.oasis.opendocument.presentation', // .odp
    'application/vnd.oasis.opendocument.spreadsheet': 'application/vnd.oasis.opendocument.spreadsheet', // .ods
    'application/vnd.oasis.opendocument.text': 'application/vnd.oasis.opendocument.text', // .odt
    'application/msword': 'application/msword', // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.ms-powerpoint': 'application/vnd.ms-powerpoint', // .ppt
    'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    'application/vnd.rar': 'application/vnd.rar', // .rar
    'application/x-tar': 'application/x-tar', // .tar
    'application/zip': 'application/zip', // .zip
    'application/x-7z-compressed': 'application/x-7z-compressed', // .7z
    'text/plain': 'text/plain'
};
export const acceptedImageTypeMap = {
    'image/gif': 'image/gif',
    'image/jpeg': 'image/jpeg',
    'image/png': 'image/png',
    'image/svg+xml': 'image/svg+xml', // .svg
    'image/webp': 'image/webp' // .webp
};
export const acceptedVideoTypeMap = {
    'video/webm': 'video/webm', // .webm
    'video/mp4': 'video/mp4',
    'video/mpeg': 'video/mpeg',
    'video/ogg': 'video/ogg', // .ogg
    'video/quicktime': 'video/quicktime' // .mov, .mp4
};
export const ContentTypeMap = {
    'text/plain': 'text/plain',
    'text/html': 'text/html',
    // 'application/json': 'application/json',
    ...acceptedAudioTypeMap,
    ...acceptedFileTypeMap,
    ...acceptedImageTypeMap,
    ...acceptedVideoTypeMap
} as const;
export type ContentType = keyof typeof ContentTypeMap;
export interface WSMessage {
    contentType: ContentType;
    fromHistory?: boolean; // this is added to messages when they're coming from history end point
    id: number;
    msgGrispiId: string;
    receivedAt: number | null;
    receiptId?: string; // this is added to outgoing messages and also exists in incoming messages that come from ws subscription
    seen: boolean;
    seenAt: number | null;
    sender: string;
    senderId: number;
    sentAt: number | null;
    text: string;
}

export type ChatMessageStatus = '' | 'waiting' | 'read' | 'received' | 'sent';

export type ChatMessageToSend = Pick<WSMessage, 'sender' | 'sentAt' | 'text'> & Required<Pick<WSMessage, 'receiptId'>>;

export type LastChatMessage = Pick<ChatMessageToSend, 'sender' | 'sentAt' | 'text'> &
    Pick<WSMessage, 'msgGrispiId' | 'senderId'> & {
        status: ChatMessageStatus;
    };

export type ChatMessage = WSMessage & Pick<LastChatMessage, 'status'>;

export enum WSMessageTypeFromSystem {
    ERROR = 'ERROR',
    INFO = 'INFO'
}

export enum WSMessageCodeType {
    CHAT_SESSION_CLOSED = 'CHAT_SESSION_CLOSED', //exchange | INFO
    MESSAGE_RECEIVED = 'MESSAGE_RECEIVED', //updates | INFO
    MESSAGE_SEEN = 'MESSAGE_SEEN', //updates | INFO
    INVALID_MESSAGE = 'INVALID_MESSAGE', //updates | ERROR
    INVALID_CHAT_SESSION = 'INVALID_CHAT_SESSION', //updates | ERROR
    NOT_AUTHORIZED = 'NOT_AUTHORIZED' //updates | ERROR
}
export type WSInfoMessageCodeType =
    | WSMessageCodeType.CHAT_SESSION_CLOSED
    | WSMessageCodeType.MESSAGE_RECEIVED
    | WSMessageCodeType.MESSAGE_SEEN;
export type WSErrorMessageCodeType =
    | WSMessageCodeType.INVALID_MESSAGE
    | WSMessageCodeType.INVALID_CHAT_SESSION
    | WSMessageCodeType.NOT_AUTHORIZED;

export type WSMessageFromSystem = {
    chatSessionId: string;
} & (
    | {
          type: WSMessageTypeFromSystem.INFO;
          code: WSInfoMessageCodeType;
      }
    | {
          type: WSMessageTypeFromSystem.INFO;
          code: WSMessageCodeType.MESSAGE_RECEIVED;
          messageId: string;
          receivedAt: number;
          subscriptionId: string;
      }
    | {
          type: WSMessageTypeFromSystem.ERROR;
          code: WSErrorMessageCodeType;
      }
);

export interface IActiveChatMessage {
    body: WSMessage;
    ticketKey: NewTicket['key'];
}

export interface Chat {
    chatId: string;
    chatSessionId: string;
    agentId?: number;
}

export type ChatEntity = Chat & {
    ticketKey: NewTicket['key'];
};

export type ChatResumeResponse = ChatEntity & {
    newChat: boolean;
    newSession: boolean;
};
